/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * Module containing Logs related components and services.
 * @preferred
 * @module LogsModule
 */

import {
    CUSTOM_ELEMENTS_SCHEMA,
    NgModule,
    NO_ERRORS_SCHEMA,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { VIPModule } from '@vmw/ngx-vip';
import { LetModule, PushModule } from '@ngrx/component';

import {
    ClrFormsModule,
    ClrSpinnerModule,
} from '@clr/angular';

import { AviCardModule } from 'nsx-alb-ui-components';

import { CoreModule } from 'ng/modules/core/core.module';
import { SharedModule } from 'ng/shared/shared.module';
import { AviFormsModule } from 'ng/modules/avi-forms/avi-forms.module';
import { MatchModule } from 'ng/modules/match/match.module';
import { DialogModule } from 'ng/modules/dialog/dialog.module';
import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';
import { RightRailModule } from 'ng/modules/right-rail/right-rail.module';
import { CinematicModule } from 'ng/modules/cinematic/cinematic.module';
import { DiagramModule } from 'ng/modules/diagram/diagram.module';
import { TooltipModule } from 'ng/modules/tooltip/tooltip.module';
import { LoaderModule } from 'ng/modules/loader/loader.module';

import {
    LogsTimeframesService,
    RecommendationDialogComponent,
    RecommendationDialogService,
    RecommendationGridComponent,
    VsLogsWafExceptionDialogService,
} from '.';

import { LogsPageComponent } from './components/logs-page/logs-page.component';
import {
    TrafficLogsActionsComponent,
} from './components/traffic-logs/traffic-logs-actions/traffic-logs-actions.component';
import { LogFilterListComponent } from './components/log-filter-list/log-filter-list.component';
import { LogDurationBarComponent } from './components/log-duration-bar/log-duration-bar.component';
import { LogDurationDetailComponent } from './components/log-duration-detail/log-duration-detail.component';
import { TrafficLogListComponent } from './components/traffic-logs/traffic-log-list/traffic-log-list.component';
import { LogPolicyGridComponent } from './components/log-policy-grid/log-policy-grid.component';
import { LogCinematicComponent } from './components/log-cinematic/log-cinematic.component';
import {
    LogCinematicTopInfoComponent,
} from './components/log-cinematic/log-cinematic-top-info/log-cinematic-top-info.component';
import {
    LogCinematicSelectionBarComponent,
} from './components/log-cinematic/log-cinematic-selection-bar/log-cinematic-selection-bar.component';
import {
    LogSignificanceComponent,
} from './components/log-cinematic/log-significance/log-significance.component';
import {
    LogCinematicSectionWrapperComponent,
    LogCinematicSubSectionWrapperComponent,
} from './components/log-cinematic/log-cinematic-section-wrapper';
import {
    LogCinematicSectionSummaryComponent,
} from './components/log-cinematic/log-cinematic-section-summary/log-cinematic-section-summary.component';
import {
    LogCinematicSectionSslComponent,
} from './components/log-cinematic/log-cinematic-section-ssl/log-cinematic-section-ssl.component';
import {
    LogCinematicSectionSecurityBotComponent,
    LogCinematicSectionSecurityComponent,
    LogCinematicSectionSecurityWafComponent,
    LogCinematicSubSectionIcapComponent,
} from './components/log-cinematic/log-cinematic-section-security';

import {
    LogCinematicSectionProtocolComponent,
} from './components/log-cinematic/log-cinematic-section-protocol/log-cinematic-section-protocol.component';
import {
    LogCinematicSectionOutOfBandComponent,
} from './components/log-cinematic/log-cinematic-section-out-of-band/log-cinematic-section-out-of-band.component';
import {
    LogCinematicSectionHeadersComponent,
} from './components/log-cinematic/log-cinematic-section-headers/log-cinematic-section-headers.component';
import {
    LogCinematicDnsOptRecordComponent,
    LogCinematicSectionDnsComponent,
    LogCinematicSubSectionDnsRequestComponent,
    LogCinematicSubSectionDnsResponseComponent,
} from './components/log-cinematic/log-cinematic-section-dns';

import {
    LogCinematicSectionDatascriptComponent,
} from './components/log-cinematic/log-cinematic-section-datascript/log-cinematic-section-datascript.component';
import {
    LogCinematicSectionConnection,
} from './components/log-cinematic/log-cinematic-section-connection/log-cinematic-section-connection.component';
import {
    LogCinematicSectionAuthenticationComponent,
    LogCinematicSubSectionJwtComponent,
    LogCinematicSubSectionNtlmComponent,
    LogCinematicSubSectionOAuthComponent,
    LogCinematicSubSectionSamlComponent,
} from './components/log-cinematic/log-cinematic-section-authentication';
import {
    LogCinematicSectionApplicationComponent,
} from './components/log-cinematic/log-cinematic-section-application/log-cinematic-section-application.component';
import {
    LogCinematicIpFieldComponent,
} from './components/log-cinematic/log-cinematic-ip-field/log-cinematic-ip-field.component';
import {
    LogCinematicEndToEndBridgeComponent,
} from './components/log-cinematic/log-cinematic-end-to-end-bridge/log-cinematic-end-to-end-bridge.component';
import {
    LogCinematicEndToEndKnotComponent,
} from './components/log-cinematic/log-cinematic-end-to-end-knot/log-cinematic-end-to-end-knot.component';
import {
    TrafficLogCinematicComponent,
} from './components/traffic-logs/traffic-log-cinematic/traffic-log-cinematic.component';
import { LogIpFieldComponent } from './components/log-ip-field/log-ip-field.component';
import {
    LogsApiRequestService,
    LogsDataTransformerService,
    LogsEffectsService,
} from './services';

import {
    LogCinematicSectionColumnDirective,
    LogEmptyFieldDirective,
    LogFieldTitleDirective,
    LogFilterableEmptyFieldDirective,
    LogFilterDirective,
    LogFilterRangeDirective,
    LogNationalFlagDirective,
    LogPartialSelectionFilterDirective,
    LogsEditableFilterInputEventsDirective,
} from './directives';

import { LogsDurationDetailService } from './services/logs-duration-detail.service';
import { LogsSearchBarDropdownService } from './services/logs-search-bar-dropdown.service';

import {
    TrafficLogsSidebarComponent,
} from './components/traffic-logs/traffic-logs-sidebar/traffic-logs-sidebar.component';
import {
    TrafficLogsCollapsedSidebarComponent,
} from './components/traffic-logs/traffic-logs-sidebar/traffic-logs-collapsed-sidebar';
import { TrafficLogsGraphComponent } from './components/traffic-logs/traffic-logs-graph/traffic-logs-graph.component';
import { LogsSignpostComponent } from './components/logs-signpost/logs-signpost.component';
import {
    LogsSignpostDefaultComponent,
} from './components/logs-signpost/logs-signpost-default/logs-signpost-default.component';
import {
    LogsSignpostDefaultViewLogListComponent,
    // eslint-disable-next-line max-len
} from './components/logs-signpost/logs-signpost-default/logs-signpost-default-view-log-list/logs-signpost-default-view-log-list.component';
import {
    LogsSignpostDefaultHeaderComponent,
    // eslint-disable-next-line max-len
} from './components/logs-signpost/logs-signpost-default/logs-signpost-default-header/logs-signpost-default-header.component';
import { LogsErrorsComponent } from './components/logs-errors/logs-errors.component';

import {
    LogsSignpostCustomComponent,
    LogsSignpostCustomEndToEndTimingComponent,
    LogsSignpostCustomEndToEndTimingDataGridComponent,
    LogsSignpostCustomEndToEndTimingHeaderComponent,
    LogsSignpostCustomGroupsComponent,
    LogsSignpostCustomSignificanceComponent,
    LogsSignpostCustomWafLatencyComponent,
    LogsSignpostCustomWafRuleComponent,
    LogsSignpostCustomWafRuleGroupsComponent,
} from './components/logs-signpost/logs-signpost-custom';

import {
    TrafficLogsSignificanceOptionsComponent,
} from './components/traffic-logs/traffic-logs-significance-options';
import { TrafficLogsSearchBarComponent } from './components/traffic-logs/traffic-logs-search-bar';
import { TrafficLogsTypeDropdownComponent } from './components/traffic-logs/traffic-logs-type-dropdown';
import {
    LogsEditableFilterComponent,
} from './components/logs-editable-filter';
import {
    TrafficLogsSearchBarDropdownComponent,
} from './components/traffic-logs/traffic-logs-search-bar-dropdown';
import {
    LogsFilterInputComponent,
} from './components/logs-filter-input';
import {
    TrafficLogsSaveSearchModalComponent,
} from './components/traffic-logs/traffic-logs-save-search-modal';
import {
    TrafficLogListExpandedContentCommunicatingIpsComponent,
    TrafficLogListExpandedContentComponent,
    TrafficLogListExpandedContentSummaryComponent,
} from './components/traffic-logs/traffic-log-list-expanded-content';

import {
    LogListExpandedContentComponent,
} from './components/log-list-expanded-content/log-list-expanded-content.component';
import {
    TrafficLogsVsDropdownComponent,
} from './components/traffic-logs/traffic-logs-vs-dropdown/traffic-logs-vs-dropdown.component';

import {
    CountryCodeToNamePipe,
    CountryFlagImageSourcePipe,
    DnsClientInfoPipe,
    DnsLoadBalancerInfoPipe,
    L4ClientInfoPipe,
    L7ClientInfoPipe,
    LogClientEnvToIconClassPipe,
    LogConnectionGridRowsPipe,
    LogHttpHeadersDiffSourcePairPipe,
    LogHttpVersionPipe,
    LogResponseCompressionInfoPipe,
    LogSignificanceReasonsPipe,
    LogSslGridRowsPipe,
    NsxAlbInfoPipe,
    PolicyRulesPipe,
    RequestDetailsPipe,
    RequestInfoPipe,
    ResponseInfoPipe,
    SignpostLogDataTransformPipe,
    SubLogHeadersDiffSourcePairPipe,
    TransformSavedSearches,
    WafRuleGroupNameFilterPipe,
} from './pipes';

const logsComponents = [
    LogCinematicComponent,
    LogCinematicDnsOptRecordComponent,
    LogCinematicEndToEndBridgeComponent,
    LogCinematicEndToEndKnotComponent,
    LogCinematicIpFieldComponent,
    LogCinematicSectionApplicationComponent,
    LogCinematicSectionAuthenticationComponent,
    LogCinematicSectionConnection,
    LogCinematicSectionDatascriptComponent,
    LogCinematicSectionDnsComponent,
    LogCinematicSectionHeadersComponent,
    LogCinematicSectionOutOfBandComponent,
    LogCinematicSectionProtocolComponent,
    LogCinematicSectionSecurityBotComponent,
    LogCinematicSectionSecurityComponent,
    LogCinematicSectionSecurityWafComponent,
    LogCinematicSectionSslComponent,
    LogCinematicSectionSummaryComponent,
    LogCinematicSectionWrapperComponent,
    LogCinematicSelectionBarComponent,
    LogCinematicSubSectionDnsRequestComponent,
    LogCinematicSubSectionDnsResponseComponent,
    LogCinematicSubSectionIcapComponent,
    LogCinematicSubSectionJwtComponent,
    LogCinematicSubSectionNtlmComponent,
    LogCinematicSubSectionOAuthComponent,
    LogCinematicSubSectionSamlComponent,
    LogCinematicSubSectionWrapperComponent,
    LogCinematicTopInfoComponent,
    LogDurationBarComponent,
    LogDurationDetailComponent,
    LogFilterListComponent,
    LogSignificanceComponent,
    LogListExpandedContentComponent,
    LogIpFieldComponent,
    LogPolicyGridComponent,
    LogsErrorsComponent,
    LogsPageComponent,
    LogsSignpostComponent,
    LogsSignpostCustomComponent,
    LogsSignpostCustomEndToEndTimingComponent,
    LogsSignpostCustomEndToEndTimingDataGridComponent,
    LogsSignpostCustomEndToEndTimingHeaderComponent,
    LogsSignpostCustomGroupsComponent,
    LogsSignpostCustomSignificanceComponent,
    LogsSignpostCustomWafLatencyComponent,
    LogsSignpostCustomWafRuleComponent,
    LogsSignpostCustomWafRuleGroupsComponent,
    LogsSignpostDefaultComponent,
    LogsSignpostDefaultHeaderComponent,
    LogsSignpostDefaultViewLogListComponent,
    RecommendationDialogComponent,
    RecommendationGridComponent,
    TrafficLogCinematicComponent,
    TrafficLogsActionsComponent,
    TrafficLogListComponent,
    TrafficLogListExpandedContentCommunicatingIpsComponent,
    TrafficLogListExpandedContentComponent,
    TrafficLogListExpandedContentSummaryComponent,
    TrafficLogsCollapsedSidebarComponent,
    TrafficLogsSidebarComponent,
    TrafficLogsGraphComponent,
    TrafficLogsSearchBarComponent,
    TrafficLogsSignificanceOptionsComponent,
    TrafficLogsTypeDropdownComponent,
    LogsEditableFilterComponent,
    TrafficLogsSearchBarDropdownComponent,
    LogsFilterInputComponent,
    TrafficLogsSaveSearchModalComponent,
    TrafficLogsVsDropdownComponent,
];

const directives = [
    LogCinematicSectionColumnDirective,
    LogEmptyFieldDirective,
    LogFieldTitleDirective,
    LogFilterableEmptyFieldDirective,
    LogFilterDirective,
    LogFilterRangeDirective,
    LogNationalFlagDirective,
    LogPartialSelectionFilterDirective,
    LogFilterRangeDirective,
    LogFilterableEmptyFieldDirective,
    LogsEditableFilterInputEventsDirective,
];

const pipes = [
    CountryCodeToNamePipe,
    CountryFlagImageSourcePipe,
    DnsClientInfoPipe,
    DnsLoadBalancerInfoPipe,
    L4ClientInfoPipe,
    L7ClientInfoPipe,
    LogClientEnvToIconClassPipe,
    LogConnectionGridRowsPipe,
    LogHttpHeadersDiffSourcePairPipe,
    LogHttpVersionPipe,
    LogResponseCompressionInfoPipe,
    LogSignificanceReasonsPipe,
    LogSslGridRowsPipe,
    NsxAlbInfoPipe,
    PolicyRulesPipe,
    RequestDetailsPipe,
    RequestInfoPipe,
    ResponseInfoPipe,
    SignpostLogDataTransformPipe,
    SubLogHeadersDiffSourcePairPipe,
    TransformSavedSearches,
    WafRuleGroupNameFilterPipe,
];

/**
 * @description Module containing Logs related components and services.
 * @author vgohil, Zhiqian Liu, Yogesh Patil
 */
@NgModule({
    declarations: [
        ...directives,
        ...logsComponents,
        ...directives,
        ...pipes,
    ],
    imports: [
        AviCardModule,
        AviFormsModule,
        CinematicModule,
        ClrFormsModule,
        ClrSpinnerModule,
        CommonModule,
        CoreModule,
        SharedModule,
        DialogModule,
        LoaderModule,
        MatchModule,
        VIPModule,
        DataGridModule,
        RightRailModule,
        DiagramModule,
        TooltipModule,
        PushModule,
        LetModule,
    ],
    exports: [
        LogsPageComponent,
        LogEmptyFieldDirective,
        TrafficLogsActionsComponent,
        TrafficLogListComponent,
        TrafficLogsCollapsedSidebarComponent,
        TrafficLogsSidebarComponent,
        TrafficLogsGraphComponent,
        TrafficLogsSearchBarComponent,
        TrafficLogsSignificanceOptionsComponent,
        TrafficLogsSearchBarDropdownComponent,
        TrafficLogsVsDropdownComponent,
        TrafficLogsTypeDropdownComponent,
        LogsFilterInputComponent,
        LogsEditableFilterComponent,
        LogsEditableFilterInputEventsDirective,
        TrafficLogCinematicComponent,
    ],
    providers: [
        CountryFlagImageSourcePipe,
        DnsClientInfoPipe,
        DnsLoadBalancerInfoPipe,
        L4ClientInfoPipe,
        L7ClientInfoPipe,
        LogClientEnvToIconClassPipe,
        LogHttpVersionPipe,
        LogSignificanceReasonsPipe,
        NsxAlbInfoPipe,
        PolicyRulesPipe,
        RequestDetailsPipe,
        RequestInfoPipe,
        ResponseInfoPipe,
        SignpostLogDataTransformPipe,
        LogsApiRequestService,
        LogsDataTransformerService,
        LogsDurationDetailService,
        LogsSearchBarDropdownService,
        LogsEffectsService,
        LogsSignpostComponent,
        LogsTimeframesService,
        RecommendationDialogService,
        VsLogsWafExceptionDialogService,
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA,
    ],
})
export class LogsModule {}
